import { useState } from "react";
import i18n from "../../../language/i18n";
import { EmptyState } from "../../../library";
import OutcomeDetail from "./OutcomeDetail";
import CheckBoxGlyph from "../CheckBoxGlyph";
import { outcomeGraphGeneralStatusStyles } from "./OutcomeGraphGeneralStatusStyles";
import { mergeOutcomeReportLists } from "../../../utilization/HolisticReportUtilization";

const OutcomeGraphGeneralStatus = (props: any) => {
  const {
    branchAssignmentOutcomeReportList = [],
    personalizedAssignmentOutcomeReportList = [],
    handwrittenOutcomeReportList = [],
    mockExamOutcomeReportList = [],
    // Destek ödevi rows (published results only — withheld ones never reach a tree).
    supportAssignmentOutcomeReportList = [],
    // Time-range filter is owned by the parent (the /reports filter bar) and
    // passed down, so it lives next to the "Ders" dropdown rather than here.
    timeFilter = "all",
    // The source selection, when the parent owns it. `undefined` (not `[]`) means "not controlled" —
    // an empty array is a real, if degenerate, selection and must not be read as its absence.
    //
    // Same two-mode contract `timeFilter` above already uses. The local chips leave rather than
    // mirror the hoisted row, because this component renders ONCE PER DERS: a learner viewing all
    // subjects got an independent chip row per coterie, and unticking a source in one left the rest
    // as they were.
    visibleSourceIds,
    // "Konu Anlatımına Git" on weak outcome rows. Threaded straight through to OutcomeDetail and
    // owned by the REPORT, not by this graph: the graph renders once per ders, so a modal here would
    // be one dialog per lesson card, and the subject-description list it resolves against is a single
    // context value for the whole screen. Absent → the tree renders exactly as it did.
    subjectDescriptionNavigation,
  } = props;
  const timeFilterWithN = timeFilter;
  const isSourceSelectionControlled = visibleSourceIds !== undefined;
  const sourceIsOn = (sourceId: string, uncontrolledValue: boolean) =>
    isSourceSelectionControlled
      ? (visibleSourceIds || []).includes(sourceId)
      : uncontrolledValue;

  const [showBranchAssignment, setShowBranchAssignment] = useState(true);
  const [showSupportAssignment, setShowSupportAssignment] = useState(true);
  const [showPersonalizedAssignment, setShowPersonalizedAssignment] =
    useState(true);
  const [showHandwritten, setShowHandwritten] = useState(true);
  const [showMockExam, setShowMockExam] = useState(true);

  const hasBranchAssignmentData =
    branchAssignmentOutcomeReportList.length > 0;
  const hasSupportAssignmentData = supportAssignmentOutcomeReportList.length > 0;
  const hasPersonalizedAssignmentData =
    personalizedAssignmentOutcomeReportList.length > 0;
  const hasHandwrittenData = handwrittenOutcomeReportList.length > 0;
  const hasMockExamData = mockExamOutcomeReportList.length > 0;
  const activeSourceCount =
    (hasBranchAssignmentData ? 1 : 0) +
    (hasSupportAssignmentData ? 1 : 0) +
    (hasPersonalizedAssignmentData ? 1 : 0) +
    (hasHandwrittenData ? 1 : 0) +
    (hasMockExamData ? 1 : 0);
  const hasMultipleSources = activeSourceCount > 1;

  // ONE fold over every enabled kind, question-weighted, windowed on each row's own date — the Genel
  // durum merge rule of the backend contract (see mergeOutcomeReportLists). The old merge glued the
  // rows together but kept the first kind's pre-computed percentages, and filtered by a row date the
  // rows never carried.
  const filteredBranchAssignmentReportList = mergeOutcomeReportLists(
    [
      sourceIsOn("BRANCH_ASSIGNMENT", showBranchAssignment)
        ? branchAssignmentOutcomeReportList
        : [],
      sourceIsOn("SUPPORT_ASSIGNMENT", showSupportAssignment)
        ? supportAssignmentOutcomeReportList
        : [],
      sourceIsOn("PERSONALIZED_ASSIGNMENT", showPersonalizedAssignment)
        ? personalizedAssignmentOutcomeReportList
        : [],
      sourceIsOn("HANDWRITTEN_EXAM", showHandwritten)
        ? handwrittenOutcomeReportList
        : [],
      sourceIsOn("MOCK_EXAM", showMockExam) ? mockExamOutcomeReportList : [],
    ],
    timeFilterWithN,
  );

  return (
    <>
      <style>{outcomeGraphGeneralStatusStyles}</style>
      {hasMultipleSources && !isSourceSelectionControlled ? (
        <div className="qbd-source-bar">
          {hasBranchAssignmentData ? (
            <div
              className="qbd-source-item"
              onClick={() => {
                setShowBranchAssignment(!showBranchAssignment);
              }}
            >
              <CheckBoxGlyph checked={showBranchAssignment} />
              <p>{i18n.t("branch_assignment_performance")}</p>
            </div>
          ) : null}
          {hasSupportAssignmentData ? (
            <div
              className="qbd-source-item"
              onClick={() => {
                setShowSupportAssignment(!showSupportAssignment);
              }}
            >
              <CheckBoxGlyph checked={showSupportAssignment} />
              <p>{i18n.t("support_assignment")}</p>
            </div>
          ) : null}
          {hasPersonalizedAssignmentData ? (
            <div
              className="qbd-source-item"
              onClick={() => {
                setShowPersonalizedAssignment(!showPersonalizedAssignment);
              }}
            >
              <CheckBoxGlyph checked={showPersonalizedAssignment} />
              <p>{i18n.t("personalized_assignment_performance")}</p>
            </div>
          ) : null}
          {hasHandwrittenData ? (
            <div
              className="qbd-source-item"
              onClick={() => {
                setShowHandwritten(!showHandwritten);
              }}
            >
              <CheckBoxGlyph checked={showHandwritten} />
              <p>{i18n.t("handwritten_exam_performance")}</p>
            </div>
          ) : null}
          {hasMockExamData ? (
            <div
              className="qbd-source-item"
              onClick={() => {
                setShowMockExam(!showMockExam);
              }}
            >
              <CheckBoxGlyph checked={showMockExam} />
              <p>{i18n.t("mock_exam_performance")}</p>
            </div>
          ) : null}
        </div>
      ) : null}

      {filteredBranchAssignmentReportList.length > 0 ? (
        <div className="qbd-report-list">
          {filteredBranchAssignmentReportList.map(
            (categoryActivityAnswerDTO, index) => (
              <div key={index}>
                <OutcomeDetail
                  categoryActivityAnswerDTO={categoryActivityAnswerDTO}
                  subjectDescriptionNavigation={subjectDescriptionNavigation}
                />
              </div>
            ),
          )}
        </div>
      ) : (
        <EmptyState compact icon="chartBar" title={i18n.t("no_report_data_for_lesson")} />
      )}
    </>
  );
};

export default OutcomeGraphGeneralStatus;
